"use client";

import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { CalendarIcon, MapPinIcon, SquarePen } from "lucide-react";
import Image from "next/image";
import EventTimer from "@/components/shared/event-timer";
import { TEvent } from "@/types/events";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { extractEventLink } from "@/types/events";
import { useState } from "react";
import { Button } from "@/components/new/button";
import { EventInfoDialog } from "@/modules/community/components/calendar/components/event-info-dialog";
import { ModalType } from "@/types/modal-type";

interface EventCardProps {
	event: TEvent;
	index?: number;
	showPeopleCount?: boolean;
	onEventDeleted?: (eventId: string) => void;
	onEventUpdated?: (updatedEvent: TEvent) => void;
	className?: string;
}

const EventCard = ({
	event,
	index,
	showPeopleCount = false,
	onEventDeleted,
	onEventUpdated,
	className,
}: EventCardProps) => {
	const [isEventDialogOpen, setIsEventDialogOpen] = useState<boolean>(false);
	const { openModal } = useModal();
	const { isLoggedIn, user } = useAuth();

	const canEdit = user && (user.id === event.authorId || user.isAdmin);

	const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		if (isLoggedIn) {
			const url = extractEventLink(event);
			if (url) {
				window.open(url, "_blank");
			}
		} else {
			openModal(ModalType.Auth);
		}
	};

	const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();
		setIsEventDialogOpen(true);
	};

	return (
		<article
			key={event.id}
			className={cn(
				"flex flex-col items-start justify-start overflow-hidden border transition-all duration-150 ease-linear hover:shadow-xl",
				className
			)}
		>
			<div className="relative w-full">
				<Image
					src={event.coverImage || "/images/image-placeholder.svg"}
					alt="Image"
					width={1080}
					height={1080}
					className="h-36 w-full bg-gray-100 object-cover"
					onClick={handleOnClick}
				/>

				{typeof index === "number" && index <= 2 && (
					<div className="absolute bottom-2 right-2 z-10">
						<EventTimer
							dateTime={new Date(event.dateTime)}
							classNames={{
								container: "!gap-[2px] !m-0",
								tile: "!gap-[1px] bg-[#1E1E1E] !w-[21px] !h-[19px] !p-4 bg-red-100",
								timeCount: "!text-[11px] font-bold !leading-[14px] text-red-500",
								timeUnit: "!text-[4px] font-extrabold leading-[6px] text-red-500",
							}}
						/>
					</div>
				)}
				{canEdit && (
					<div className="absolute right-3 top-36 z-20">
						<Button
							variant="ghost"
							className="p-0 text-blue-500 hover:bg-transparent hover:text-blue-700"
							onClick={handleButtonClick}
							title="Edit event"
						>
							<SquarePen className="h-5 w-5 text-brand-primary" />
						</Button>
					</div>
				)}
			</div>

			<div
				className={cn("h-full w-full cursor-pointer", event.isPremium ? `bg-premium-event-bg` : ``)}
				onClick={handleOnClick}
			>
				<div className="flex h-full flex-col justify-between">
					<div className="px-2">
						<div className="mb-3 mt-2 text-xxs">
							{event.isFeatured && (
								<span className="relative z-10 rounded-full bg-brand-primary px-3 py-1.5 font-medium text-white">
									Featured
								</span>
							)}
						</div>
						<div className="group relative">
							<span className={cn("text-[9px]", event.isPremium ? `text-white` : `text-[#1E1E1E]`)}>
								{event.type}
							</span>
							<h3
								className={cn(
									"text-lg font-semibold leading-6",
									event.isPremium
										? `bg-premium-event-title bg-clip-text text-transparent transition-all duration-300 hover:bg-premium-event-title-hover`
										: `text-gray-900 group-hover:text-brand-primary`
								)}
							>
								<button className="block text-left">
									<span className="absolute inset-0" />
									{event.title}
								</button>
							</h3>
						</div>
						<div className="my-4 flex flex-col gap-y-4 text-xs">
							<div className="flex cursor-default items-center gap-2">
								<span>
									<CalendarIcon className="h-4 w-4 text-blue-500" />
								</span>
								<time
									dateTime={event.dateTime}
									className={cn(
										"break-words leading-4",
										event.isPremium ? `text-white` : `text-gray-500`
									)}
								>
									{format(parseISO(event.dateTime), "MMMM d, yyyy")}{" "}
								</time>
							</div>
							<div className="flex cursor-default items-center gap-2">
								<span>
									<MapPinIcon className="h-4 w-4 text-blue-500" />
								</span>
								<p
									className={cn(
										"break-words leading-4",
										event.isPremium ? `text-white` : `text-gray-500`
									)}
								>
									{event.location === "ONLINE"
										? event.location
										: event.rawEventData.address && event.rawEventData.address.venueName}
								</p>
							</div>
							<div className="flex flex-wrap items-start gap-x-2">
								{event?.tags?.map((tag) => (
									<span
										key={tag}
										className="w-max rounded-sm border bg-[#E8DEEE] px-[6px] py-[3px] text-[6px] text-[#181616]"
									>
										{tag}
									</span>
								))}
							</div>
						</div>
					</div>

					{showPeopleCount ? (
						<div className="flex items-center justify-between border-t p-4">
							<div className="flex items-center justify-start gap-x-2">
								<div className="flex items-center -space-x-4">
									<Image
										src="/images/person-1.jpg"
										alt="person image"
										width={100}
										height={100}
										className="h-9 w-9 min-w-[36px] rounded-full border-2 border-white object-cover"
									/>
									<Image
										src="/images/person-2.jpg"
										alt="person image"
										width={100}
										height={100}
										className="h-9 w-9 min-w-[36px] rounded-full border-2 border-white object-cover"
									/>
								</div>
								<p
									className={`text-center text-xs ${
										event.isPremium ? `text-white` : `text-gray-600`
									}`}
								>
									<span className={`${event.isPremium ? `text-white` : `text-brand-primary`}`}>
										{event.peopleCount}+
									</span>{" "}
									people going
								</p>
							</div>

							<button
								onClick={(e) => {
									e.stopPropagation();
									handleOnClick(e);
								}}
								className={`block rounded-lg border px-4 py-2 text-sm ${
									event.isPremium
										? `border-none bg-premium-event-gold-button font-medium text-black transition-all duration-300 hover:opacity-90`
										: `bg-brand-primary text-white`
								} `}
							>
								View Details
							</button>
						</div>
					) : null}
				</div>
			</div>

			<EventInfoDialog
				event={event}
				isOpen={isEventDialogOpen}
				onClose={() => setIsEventDialogOpen(false)}
				onEventDeleted={() => {
					onEventDeleted?.(event.id);
					setIsEventDialogOpen(false);
				}}
				onEventUpdated={(updatedEvent) => {
					onEventUpdated?.(updatedEvent);
					setIsEventDialogOpen(false);
				}}
			/>
		</article>
	);
};

export default EventCard;
